import React, { useEffect, useState } from 'react';
import { renderToString } from 'react-dom/server';
import { useUser } from '@src/state/user/hooks';
import { storage } from '@uhg-abyss/web/tools/storage';
import { styled } from '@uhg-abyss/web/tools/styled';
import { Button } from '@src/common/Button';
import { PageLayout } from '@src/common/PageLayout';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { useSteps } from '@src/state/steps';
import { useTinRegistration } from '@src/state/tinRegistration';
import { useForm as useAbyssForm } from '@uhg-abyss/web/hooks/useForm';
import { Loading } from '@src/common/Loading';
import {
  getSubmitRequestObj,
  getErrorFields,
  getSessionRequestObj,
  checkSubmissionReady,
} from '@src/state/tinRegistration/utils';
import { event as adobeTaggingEvent, event } from '@uhg-abyss/web/tools/event';

import isEmpty from 'lodash/isEmpty';
import { config } from '@uhg-abyss/web/tools/config';
import { print } from '@src/app/App/Content/Confirmation/print';
import { useTinDrafts } from '@src/state/superUserDraft';
import { useLagoon } from '@src/app/tools/lagoon';
import { TinHeader } from '../TinHeader';
import { W9Upload } from './W9Upload';
import { PracticeData } from './PracticeData';
import { Attestation } from './Attestation';
import { Locations } from './Locations';
import { SignatureBox } from './SignatureBox/SignatureBox';
import { Table } from './Table';
import { LagoonContext } from '@src/AppContext/LagoonContext';

const PageButtons = styled('div', {
  width: '11%',
  marginTop: '2em',
  paddingBottom: '25px',
  fontWeight: 600,
  fontSize: '16px',
  color: '$primary1',
  fontFamily: '$heading',
  display: 'inline-flex',
  marginRight: '15px',
});

const PageDraftButton = styled('div', {
  width: 'fitContent',
  marginTop: '2em',
  paddingBottom: '25px',
  float: 'right',
  color: '$primary1',
});

const PageDraftButtonFont = styled('div', {
  fontSize: '16px',
  fontWeight: 600,
  fontFamily: '$heading',
});

const SaveDraftSuccessMessage = styled('div', {
  fontSize: '13px',
  color: '$success1',
  textAlign: 'center',
});

const SaveDraftFailedMessage = styled('div', {
  fontSize: '13px',
  textAlign: 'center',
  color: '$error1',
});

const Space = styled('div', {
  marginTop: '30px',
});
export const TinReview = () => {
  const lagoonContext = React.useContext(LagoonContext);
  const featureToggles = lagoonContext.getTable('feature-toggles');

  const userInfo = useUser.state();
  const abyssForm = useAbyssForm();
  const isSuperUser = storage.local.get(userInfo.uuid);
  const aarpVal = featureToggles.getRow('US5661375_AARP')?.active;
  const practiceInfo = abyssForm.getValues('practiceInformation');

  const w9Form = abyssForm.getValues('w9form');
  const address = abyssForm.getValues('address');
  const registration = abyssForm.getValues('searchRegistration');
  const signatureType = abyssForm.getValues('attestation.signatureRadio');
  const attestName = abyssForm.getValues('attestation.submitterName');
  const attestTitle = abyssForm.getValues('attestation.jobTitle');
  const attestPhone = abyssForm.getValues('attestation.phoneNumber');
  const attestMode = abyssForm.getValues('attestation.commPreference');
  const attestClaimSubmitted = abyssForm.getValues('attestation.submitClaim');
  const attestClaimDate = abyssForm.getValues('attestation.claimDate');
  const attestHealthProgram = abyssForm.getValues('attestation.healthProgram');
  const { submitTin } = useTinRegistration.actions();
  const { submitTinSaga } = useTinRegistration.state();
  const [downloading, setDownloading] = useState(false);
  const { documentIds, trackingNumber, isDraftDisabled } =
    useTinRegistration.state();
  const { saveAsDraft } = useTinRegistration.actions();
  const attestation = abyssForm.getValues('attestation');
  const { saveSuperUserDraft } = useTinDrafts.actions();

  const tNum = trackingNumber;
  const router = useRouter();
  const attestEmail = userInfo.email;

  const billingTin = registration ? registration.billingTin : '';

  const getJobTitle = (jobTitle) => {
    switch (jobTitle) {
      case 'administrativeStaff':
        return 'Administrative staff';
      case 'supportStaff':
        return 'Support staff';
      case 'clinician':
        return 'Clinician';
      case 'physician':
        return 'Physician';
      default:
        return '';
    }
  };

  const handlePDF = () => {
    const fileName = 'PreTinRegistration';
    event('DOWNLOAD_PDF', { fileName });
    const reviewPage = document.getElementById('print-review');
    setDownloading(true);

    const info = {
      name: attestName || '',
      title: getJobTitle(attestTitle),
      email: attestEmail || '',
      phone: attestPhone || '',
      contact: attestMode || '',
      claimSubmitted: attestClaimSubmitted || '',
      aarpToggle: aarpVal,
      claimDate: attestClaimDate || '',
      healthProgram:
        attestHealthProgram &&
        (attestHealthProgram === 'aarp' || attestHealthProgram === 'uhc')
          ? 'Yes'
          : 'No',
    };
    const table = new DOMParser().parseFromString(
      renderToString(<Table info={info} />),
      'text/html'
    );
    if (print && print.savePDFReview) {
      print.savePDFReview(reviewPage, table.body.firstChild, fileName, () => {
        setDownloading(false);
      });
    } else {
      setDownloading(false);
    }
  };

  const {
    pagesVisited,
    pagesSubmitted,
    setStep,
    addPageVisited,
    addPageSubmitted,
  } = useSteps();

  useEffect(() => {
    const pageName = 'epvsecure:tin registration: step4-attestation';
    const siteSectionLevel = {
      siteSectionLevel1: 'tin registration',
      siteSectionLevel2: 'step4-attestation',
      siteSectionLevel3: '',
      siteSectionLevel4: '',
      siteSectionLevel5: '',
    };
    event('ON_PAGE_LOAD', {
      userInfo,
      pageName,
      billingTin,
      siteSectionLevel,
    });
    if (pagesVisited && !pagesVisited.includes('TinReview')) {
      const pages = pagesVisited.concat('TinReview');
      addPageVisited(pages);
    }
    if (!pagesVisited) {
      const pages = ['TinReview'];
      addPageVisited(pages);
    }
    setStep(4);
  }, []);

  const goBack = () => {
    event('ATTESTATION_BACK');
    router.navigate('/tin-registration/step/3');
  };

  const continueRegistration = () => {
    event('ATTESTATION_SUBMIT');
    abyssForm.validate(
      'attestation',
      (values) => {
        event('ATTESTATION_COMPLETE', {
          userInfo,
          practiceInfo,
          address,
          billingTin,
          trackingNumber: tNum,
        });
        if (pagesSubmitted && !pagesSubmitted.includes('TinReview')) {
          const pages = pagesSubmitted.concat('TinReview');
          addPageSubmitted(pages);
        }
        if (!pagesSubmitted) {
          const pages = ['TinReview'];
          addPageSubmitted(pages);
        }
        const updatedvalues = values;
        updatedvalues.emailAddress = userInfo.email;
        const reqBody = getSubmitRequestObj({
          w9Form,
          practiceInfo,
          address,
          attestation: values,
          documentIds,
          signatureType,
          aarpToggle: aarpVal,
        });
        let submissionReady = true;
        if (config('US3830334_SIGNATURE_FIX')) {
          submissionReady = checkSubmissionReady({
            signatureType,
            documentIds,
          });
        }

        if (submissionReady) {
          submitTin(
            {
              billingTin: registration.billingTin,
              requestId: registration.requestId || null,
              trackingNumber: tNum,
              ...reqBody,
            },
            (response) => {
              router.navigate('/tin-registration/confirmation');
            },
            (error) => {
              event('API_TRANSACTION_ERROR', {
                code: error.faultCode,
                message: error.message,
              });
              router.navigate('/tin-registration/confirmation');
            }
          );
        }
      },
      (error) => {
        const fields = getErrorFields(error);
        if (!isEmpty(fields)) {
          event('FORM_VALIDATION_ERROR', { fields });
        }
      }
    );
  };

  const [saveDraftFailed, setSaveDraftFailed] = useState(false);
  const [saveDraftSuccess, setSaveDraftSuccess] = useState(false);

  const saveDraft = () => {
    const reqBody = getSessionRequestObj({
      w9Form,
      practiceInfo,
      address,
      attestation,
      documentIds,
      signatureType,
    });

    const interactionName = 'attestation widget';
    const interactionValue = 'save as draft';
    const eventInteraction = 'interaction';
    adobeTaggingEvent('SAVE_AS_DRAFT', {
      eventInteraction,
      interactionName,
      interactionValue,
    });

    const requestId = registration ? registration.requestId : '';
    setSaveDraftSuccess(false);
    setSaveDraftFailed(false);
    if (!isSuperUser) {
      saveAsDraft(
        {
          billingTin,
          requestId,
          trackingNumber: tNum,
          ...reqBody,
        },
        (response) => {
          setSaveDraftSuccess(true);
        },
        (error) => {
          setSaveDraftFailed(true);
        }
      );
    } else {
      saveSuperUserDraft(
        {
          billingTin,
          requestId,
          optumId: registration.optumId,
          trackingNumber: tNum,
          ...reqBody,
        },
        (response) => {
          setSaveDraftSuccess(true);
        },
        (error) => {
          setSaveDraftFailed(true);
        }
      );
    }
  };

  return (
    <PageLayout>
      <Loading
        isLoading={submitTinSaga.isLoading}
        message="Submitting registration..."
      >
        <TinHeader
          callPdf={handlePDF}
          downloading={downloading}
          trackingNumber={trackingNumber}
        />
        <div id="print-review">
          <Space id="w9upload-review">
            <W9Upload confirmationFlag="false" />
          </Space>
          <Space id="practice-data-review">
            <PracticeData />
          </Space>
          <Space id="locations-review">
            <Locations />
          </Space>
          <Space id="attestation-data-review">
            <Attestation />
          </Space>
        </div>
        <Space>
          <SignatureBox />
        </Space>
        <PageButtons>
          <Layout.Group>
            <Button
              type="button"
              id="attestation-back-button"
              aria-label="click here to go back"
              data-testid="start-button"
              onClick={goBack}
              width="100%"
              variant="outline"
            >
              Back
            </Button>
            <Button
              type="button"
              id="attestation-submit-button"
              aria-label="click here to continue"
              data-testid="start-button"
              onClick={continueRegistration}
              width="100%"
              isDisabled={isSuperUser}
            >
              Submit
            </Button>
          </Layout.Group>
        </PageButtons>
        <PageDraftButton>
          <PageDraftButtonFont>
            <Button
              type="button"
              id="draft-continue-button"
              aria-label="click here to save as draft"
              data-testid="draft-button"
              onClick={saveDraft}
              width="100%"
              variant="outline"
              isDisabled={isDraftDisabled}
            >
              Save as draft
            </Button>
          </PageDraftButtonFont>
          {saveDraftSuccess && (
            <SaveDraftSuccessMessage id="savedraft-success">
              Saved successfully.
            </SaveDraftSuccessMessage>
          )}
          {saveDraftFailed && (
            <SaveDraftFailedMessage id="savedraft-failed">
              Failed to save.
            </SaveDraftFailedMessage>
          )}
        </PageDraftButton>
      </Loading>
    </PageLayout>
  );
};
